import React, { useEffect, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router";
import { Col, Collapse, Divider, Form, Row, Tooltip } from "antd";
import { LeftOutlined } from "@ant-design/icons";
import moment from "moment";
import { useOfferContext } from "../context/OfferContext";
import { ReactComponent as VersionsHiddenIcon } from "../../../img/versions_icons/versionsHidden_icon.svg";
import { ReactComponent as VersionsShownIcon } from "../../../img/versions_icons/versionsShown_icon.svg";
import { ReactComponent as DotsIcon } from "../../../img/3dots.svg";
import type { IOfferParamsFormData } from "../context/types";
import { useAppDispatch, useAppSelector } from "../../..";
import {
  fetchOfferVersionsByIdWithParams,
  updateOfferVersion,
  closeOfferVersion,
  offeringActions,
} from "../../../store/slices/offering/offering";
import {
  infinityDateHttpFormat,
  toHttpFormatStartOfDay,
  checkIsLoading,
  toHttpFormatEndOfDay,
  getDictionaryItemById,
} from "../../../lib/utils";
import { ILoadByWithPromise, IChangingMode, Offer } from "../../../lib/types";
import { OfferParamsView } from "../../../components/OfferDetails/OfferParamsView/OfferParamsView";
import { OfferParamsEdit } from "../../../components/OfferDetails/OfferParamsEdit/OfferParamsEdit";
import HistoryAndPropertyModal from "../../../components/HistoryAndPropertyModal/HistoryAndPropertyModal";
import { MENU_ADMIN_OFFERING_WRITE } from "../../../lib/const/permissions";
import { hasAccess } from "../../../components/RolesModule/hasAccess";
import { DictionaryDto } from "../../../lib/interfaces";
import { OFFER_STATUSES } from "../../../store/slices/offering/const";
import { validateOfferNameUniqueness } from "../utils/validateOfferNameUniqueness";
import { IChangingRecordKeyNumber, IViewModeExtended } from "../../../lib/types/IViewMode";
import { CharacteristicsContainer } from "../../../modules/policies";
import { getCharFormatItem } from "../../../modules/policies/services/getCharFormatItem";
import { useCharacteristicsInitialisation } from "../../../modules/policies/pages/CharacteristicsContainer/utils/useCharacteristicsInitialisation";
import "./OfferParamsContainer.less";

/* ------------------------------------------------------ Начало компонента ------------------------------------------------------ */

export const OfferParamsContainer = () => {
  const {
    activeOfferVersion,
    setActiveOfferVersion,
    offerParamsForm,
    searchListValues,
    offerListSelectMode,
    updateTaxRate,
  } = useOfferContext();
  const { search } = useLocation();
  const searchParams = new URLSearchParams(search);
  const onDateFromUrl = searchParams.get("onDate")?.replace(" ", "+");
  const history = useHistory();
  const dispatch = useAppDispatch();
  // const { pathname, search } = useLocation();
  const { offer, offerVersions, offerLoading, offerVersionsLoading, serviceTypeList } = useAppSelector(
    (state) => state.offering
  );

  const { offeringStatuses } = useAppSelector((state) => state.dicts);

  const toOfferList = () => {
    history.push("/offer/list");
  };

  const [historyModalVisible, setHistoryModalVisible] = useState(false);
  const [objectId, setObjectId] = useState<number | null>(null);

  const showHistoryModal = () => {
    setHistoryModalVisible(true);
    setObjectId(offer?.id || null);
  };

  const loadOfferVersions: ILoadByWithPromise = (id) =>
    dispatch(
      fetchOfferVersionsByIdWithParams({
        id,
        searchParams: {
          allObjectsVersions: true,
          withChars: true,
        },
      })
    );

  const [isVersionsOpen, setIsVersionsOpen] = useState<boolean>(false);
  const [isCollapseOpen, setIsCollapseOpen] = useState<boolean>(true);

  const toggleCollapse = () => {
    setIsCollapseOpen((prev) => !prev);
  };

  // Флаг редактируемой версии [индекс в массиве версий, mode]
  const [childChangingMode, setChildChangingMode] = useState<IChangingRecordKeyNumber>();

  // ---> Характеристики <---
  const {
    fromDateCharacteristic,
    onChangeFromDateCharacteristic,
    onCheckFieldsError,
    fieldsError,
    infoCharacteristic,
    charValuesCharacteristic,
    productCharValuesCharacteristic,
    setInfoCharacteristic,
    setCharValuesCharacteristic,
    setProductCharValuesCharacteristic,
  } = useCharacteristicsInitialisation({
    initialFromDate: childChangingMode?.[1] === "add" ? undefined : activeOfferVersion?.fromDate,
  });
  // ---> Характеристики <---

  const changeViewMode = (params?: IChangingRecordKeyNumber) => {
    setChildChangingMode(params);
  };

  const onClickVersionIcon = (e: React.MouseEvent<SVGSVGElement, MouseEvent>) => {
    e.stopPropagation();
    if (isVersionsOpen) {
      dispatch(offeringActions.cleanOfferVersions());
      setChildChangingMode(undefined);
      setActiveOfferVersion(offer);
      setIsVersionsOpen(false);
    } else if (offer?.id) {
      loadOfferVersions(offer.id).then((res) => {
        if (res.payload) {
          const findActiveVersion = res.payload.find(
            (version) => version.fromDate === activeOfferVersion?.fromDate
          );
          if (findActiveVersion) {
            setActiveOfferVersion(findActiveVersion);
          }
        }
      });
      setIsVersionsOpen(true);
    }
  };

  const initialOfferParamsValues = useMemo((): Partial<IOfferParamsFormData> => {
    const version = childChangingMode && offerVersions[childChangingMode[0]];
    const fromDate = version?.fromDate ? moment(version.fromDate) : moment();
    const toDate = version?.toDate ? moment(version.toDate) : moment(infinityDateHttpFormat);
    const baseValues = {
      extName: version?.extName,
      name: version?.name,
      serviceTypeId: version?.serviceTypeId,
      offeringTypeId: version?.offeringTypeId,
      agreementId: version?.agreementId,
      prolongTypeId: version?.prolongTypeId,
      lifespanPeriodId: version?.lifespanPeriodId,
      lifespanPeriodQty: version?.lifespanPeriodQty,
      isStandalone: version?.isStandalone,
      offeringStatusId: version?.offeringStatusId,
      tariffModelId: version?.tariffModelId,
      bundleTypeId: version?.bundleTypeId,
      periodAlignmentId: version?.periodAlignmentId,
    };
    switch (childChangingMode?.[1]) {
      case "edit":
        return {
          ...baseValues,
          fromDate,
          toDate,
        };
      case "add":
        return {
          ...baseValues,
          fromDate: undefined,
          toDate,
        };
      case "close":
        return {
          ...baseValues,
          fromDate,
          toDate,
        };
      default:
        return {};
    }
  }, [offerVersions, childChangingMode]);

  // Установка исходных значений параметров предложения для режимов изменения
  useEffect(() => {
    offerParamsForm.setFieldsValue(initialOfferParamsValues);
  }, [initialOfferParamsValues]);

  // Сброс значений формы по выходу
  useEffect(() => {
    return () => {
      offerParamsForm.resetFields();
    };
  }, []);

  // Изменение зависимых полей при измененении бизнес-модели
  const onChangeTariffModelId = (id: number) => {
    const periodAlignmentId = id === 2 ? 2 : undefined;
    offerParamsForm.setFieldsValue({ periodAlignmentId });
  };

  const sumbitUpdateOfferVersion = async (values: IOfferParamsFormData) => {
    const force = childChangingMode?.[1] === "edit";
    const isOfferNameUnique = await validateOfferNameUniqueness({
      name: values.name,
      viewMode: childChangingMode?.[1],
      offerVersions,
    });
    if (!isOfferNameUnique) {
      offerParamsForm.setFields([
        {
          name: "name",
          errors: [""],
        },
      ]);
      return;
    }
    const version = childChangingMode && offerVersions[childChangingMode[0]];
    const fromDate = force ? version?.fromDate : toHttpFormatStartOfDay(values.fromDate);

    const isValid = onCheckFieldsError([
      "infoCharacteristic",
      "charValuesCharacteristic",
      "productCharValuesCharacteristic",
    ]);

    if (!isValid) {
      return;
    }

    const info = infoCharacteristic.map((el) => getCharFormatItem(el, 1043));
    const charValues = charValuesCharacteristic.map((el) => getCharFormatItem(el));
    const productCharValues = productCharValuesCharacteristic.map((el) =>
      getCharFormatItem(el, 1032)
    );

    const editedOffer: Offer = {
      ...version,
      extName: values.extName,
      name: values.name,
      offeringTypeId: values.offeringTypeId,
      agreementId: values.agreementId,
      prolongTypeId: values.prolongTypeId,
      lifespanPeriodId: values.lifespanPeriodId,
      lifespanPeriodQty: values.lifespanPeriodQty,
      isStandalone: values.isStandalone,
      offeringStatusId: values.offeringStatusId,
      periodAlignmentId: values?.periodAlignmentId,
      tariffModelId: values?.tariffModelId,
      fromDate,
      serviceProfile: version?.serviceProfile || {},
      bundleTypeId: values.bundleTypeId,
      info,
      charValues,
      productCharValues,
    };
    const reloadOfferFromDate = force
      ? toHttpFormatStartOfDay(onDateFromUrl)
      : toHttpFormatStartOfDay(values.fromDate);
    dispatch(updateOfferVersion({ offer: editedOffer, force, reloadOfferFromDate })).then(() => {
      setChildChangingMode(undefined);
    });
  };

  const submitCloseOfferVersion = () => {
    const values: IOfferParamsFormData = offerParamsForm.getFieldsValue();
    const fromDate = values.toDate && toHttpFormatEndOfDay(values.toDate);
    if (fromDate) {
      dispatch(closeOfferVersion({ id: offer?.id!, fromDate })).then(() => {
        offer?.id &&
          loadOfferVersions(offer.id).then(() => {
            setChildChangingMode(undefined);
          });
      });
    }
  };

  const onSubmitOfferParamsEdit = (values: IOfferParamsFormData) => {
    if (childChangingMode) {
      const mode: IChangingMode = childChangingMode[1];
      switch (mode) {
        case "edit":
          sumbitUpdateOfferVersion(values);
          break;
        case "add":
          sumbitUpdateOfferVersion(values);
          break;
        case "close":
          submitCloseOfferVersion();
          break;
      }
    }
  };

  // Добавление при создании версии
  const enrichedOfferVersions =
    useMemo(() => {
      if (!childChangingMode) return offerVersions;
      if (childChangingMode[1] === "add") {
        const [pos] = childChangingMode;
        const baseVersion = offerVersions[pos];
        const firstPart = offerVersions.slice(0, pos);
        const secondPart = offerVersions.slice(pos);
        return [...firstPart, baseVersion, ...secondPart];
      }
      return offerVersions;
    }, [offerVersions, childChangingMode]) || [];

  const allowedOfferingStatusIds = useMemo((): OFFER_STATUSES[] => {
    const { PREPARE, ACTIVE, ARCHIVE, SUSPENDED, ARCHIVE_PROLONGABLE } = OFFER_STATUSES;
    const allStatuses = Object.values(OFFER_STATUSES).filter(
      (value) => typeof value === "number"
    ) as number[];
    if (!childChangingMode || enrichedOfferVersions.length === 0) return allStatuses;

    const [idx, mode] = childChangingMode;
    if (mode === "add") {
      const parentOfferStatusId: OFFER_STATUSES = enrichedOfferVersions[idx + 1].offeringStatusId;
      switch (parentOfferStatusId) {
        case PREPARE:
          return [PREPARE, ACTIVE, ARCHIVE, SUSPENDED, ARCHIVE_PROLONGABLE];
        case ACTIVE:
          return [PREPARE, ACTIVE, ARCHIVE, SUSPENDED, ARCHIVE_PROLONGABLE];
        case ARCHIVE:
          return [ARCHIVE];
        case SUSPENDED:
          return [PREPARE, ACTIVE, ARCHIVE, SUSPENDED, ARCHIVE_PROLONGABLE];
        case ARCHIVE_PROLONGABLE:
          return [ARCHIVE, ARCHIVE_PROLONGABLE];
      }
    }
    return allStatuses;
  }, [enrichedOfferVersions, childChangingMode]);

  const allowedOfferingStatuses = useMemo((): DictionaryDto<OFFER_STATUSES>[] => {
    if (!childChangingMode) return offeringStatuses;
    return offeringStatuses.filter(({ id }) => allowedOfferingStatusIds.includes(id));
  }, [childChangingMode, allowedOfferingStatusIds]);

  const getCharacteristicView = (offer, mode: IViewModeExtended, isChild?: boolean) => {
    return (
      <div className={`${isChild ? "row_child_char row_child" : ""}`}>
        <CharacteristicsContainer
          objectTypeId={1043}
          mode={mode}
          title="Характеристики Предложения"
          tableData={infoCharacteristic}
          setTableData={setInfoCharacteristic}
          dataSaved={offer?.info}
          fromDateCharacteristic={fromDateCharacteristic}
          messageWarningNotFromDate="Необходимо выбрать Дату создания предложения"
          fieldsError={fieldsError}
        />
        <CharacteristicsContainer
          mode={mode}
          title="Параметры доступности для Клиента"
          isInheritableObject
          isSelectedObjectId
          tableData={charValuesCharacteristic}
          setTableData={setCharValuesCharacteristic}
          dataSaved={offer?.charValues}
          fromDateCharacteristic={fromDateCharacteristic}
          messageWarningNotFromDate="Необходимо выбрать Дату создания предложения"
          isDefaultDisabled
          disabledValidation
          fieldsError={fieldsError}
        />
        <CharacteristicsContainer
          objectTypeId={1032}
          mode={mode}
          title="Параметры Предложения (характеристики Продукта)"
          isInheritableObject
          tableData={productCharValuesCharacteristic}
          setTableData={setProductCharValuesCharacteristic}
          dataSaved={offer?.productCharValues}
          valuesTitle="Допустимые значения"
          fromDateCharacteristic={fromDateCharacteristic}
          messageWarningNotFromDate="Необходимо выбрать Дату создания предложения"
          disabledValidation
          fieldsError={fieldsError}
        />
      </div>
    );
  };

  return (
    <div className="offer-params-container">
      <h1 id="offering-params-title">
        <Tooltip title="К списку предложений">
          <LeftOutlined onClick={toOfferList} className="offer-params-container__backBtn" />
        </Tooltip>
        Просмотр предложения
      </h1>
      <Divider className="offer-params-container__upperDivider" />
      <Collapse activeKey={isCollapseOpen ? ["1"] : []} bordered={false} onChange={toggleCollapse}>
        <Collapse.Panel
          key="1"
          header={
            <Row justify="space-between" className="offer-params-container__pageTitle">
              <h3>
                {`Предложение для ${
                  getDictionaryItemById(serviceTypeList, offer?.serviceType?.id)?.name
                }`}
              </h3>
              {isCollapseOpen && (
                <Row align="middle" gutter={7}>
                  <Col>
                    {isVersionsOpen && <VersionsShownIcon onClick={onClickVersionIcon} />}
                    {!isVersionsOpen && <VersionsHiddenIcon onClick={onClickVersionIcon} />}
                  </Col>
                  <Col>
                    <DotsIcon
                      onClick={(e) => {
                        e.stopPropagation();
                        showHistoryModal();
                      }}
                    />
                  </Col>
                </Row>
              )}
            </Row>
          }
        >
          {offer && (
            <OfferParamsView
              offer={offer}
              isLoading={checkIsLoading(offerLoading, offerVersionsLoading)}
            />
          )}
          {!isVersionsOpen && <>{getCharacteristicView(activeOfferVersion, "view", false)}</>}
          {isVersionsOpen &&
            enrichedOfferVersions.map((version, idx) => (
              <div
                key={idx}
                style={
                  version.fromDate === activeOfferVersion?.fromDate
                    ? { border: "2px solid #4cf" }
                    : undefined
                }
                onClick={() => {
                  setActiveOfferVersion(version);
                }}
              >
                {childChangingMode?.[0] !== idx && (
                  <>
                    <OfferParamsView
                      isChild
                      changeViewMode={changeViewMode}
                      idx={idx}
                      offer={version}
                      isLatestVersion={idx === 0}
                      isEditingDisabled={Boolean(childChangingMode)}
                      isLoading={checkIsLoading(offerVersionsLoading)}
                    />
                    {version.fromDate === activeOfferVersion?.fromDate &&
                      getCharacteristicView(version, "view", true)}
                  </>
                )}
                {childChangingMode?.[0] === idx && (
                  <>
                    <Form
                      form={offerParamsForm}
                      labelAlign="left"
                      onFinish={onSubmitOfferParamsEdit}
                    >
                      <OfferParamsEdit
                        viewMode={childChangingMode[1]}
                        offer={version}
                        onCancel={() => setChildChangingMode(undefined)}
                        onSubmit={offerParamsForm.submit}
                        isLoading={checkIsLoading(offerVersionsLoading)}
                        onNameBlur={validateOfferNameUniqueness}
                        allowedOfferingStatuses={allowedOfferingStatuses}
                        offerParamsForm={offerParamsForm}
                        searchListValues={searchListValues}
                        offerListSelectMode={offerListSelectMode}
                        updateTaxRate={updateTaxRate}
                        offerVersions={offerVersions}
                        onChangeTariffModelId={onChangeTariffModelId}
                        onChangeFromDateCharacteristic={onChangeFromDateCharacteristic}
                      />
                    </Form>
                    {getCharacteristicView(version, childChangingMode[1], true)}
                  </>
                )}
              </div>
            ))}
        </Collapse.Panel>
      </Collapse>
      <HistoryAndPropertyModal
        objectTypeSysname="offering"
        objectId={objectId}
        handleHideModal={() => setHistoryModalVisible(false)}
        visibleModal={historyModalVisible}
        hasAccessToEdit={hasAccess(MENU_ADMIN_OFFERING_WRITE)}
      />
    </div>
  );
};
